import { action, makeObservable, observable, reaction } from 'mobx';
import { isMobile, notificationStore, userSettingsStore } from '@pascal/pascal-core';
import {
  kenoNumbers,
  SELECTED_NUMBERS_MAX_COUNT_KENO10,
  SELECTED_NUMBERS_MAX_COUNT_KENO8,
  TAB_INDEX,
} from '../core/constants/gameConstants';
import { applicationStore } from './applicationStore';
import { userInfoStore } from './userInfoStore';
import { parseNumberPrecisionByDoteCount } from '../core/helpers/parseNumberByPrecision';
import { soundService } from '../services/baseSoundService';
import { BetStatus } from '../core/constants/betStatus';
import {
  AddTicketsCode,
  MaxNumberCount8,
  MaxNumberCount10,
} from '../core/constants/betResponseCodes';

class KenoBetStore {
  isBetsClosed = false;

  isOpenNumpad = false;

  selectedNumbers = [];

  winNumbersForTickets = [];

  kenoOdds = {};

  kenoOddsBySelectedNumbersCount = {};

  winNumbers = [];

  winAmount = 0;

  winOdd = 0;

  nonSelectedNumbers = kenoNumbers;

  lastResultsOdd = [];

  numbersCountInTicket = SELECTED_NUMBERS_MAX_COUNT_KENO10;

  maxNumbersCountInTicket = SELECTED_NUMBERS_MAX_COUNT_KENO10;

  tickets = [];

  selectedTicketsCount = 10;

  lastTickets = [];

  reBetModel = null;

  hotNumbers = [];

  coldNumbers = [];

  roundId = null;

  isShowMobileResult = false;

  totalWinAmount = null;

  liveBetActiveTabIndex = TAB_INDEX.AllBets;

  isBetButtonActive = true;

  maxWinAmount = null;

  isStopAnimation = false;

  constructor() {
    makeObservable(this, {
      winOdd: observable,
      roundId: observable,
      winAmount: observable,
      winNumbers: observable,
      hotNumbers: observable,
      reBetModel: observable,
      coldNumbers: observable,
      maxWinAmount: observable,
      isOpenNumpad: observable,
      isBetsClosed: observable,
      lastResultsOdd: observable,
      totalWinAmount: observable,
      isStopAnimation: observable,
      selectedNumbers: observable,
      isBetButtonActive: observable,
      isShowMobileResult: observable,
      nonSelectedNumbers: observable,
      winNumbersForTickets: observable,
      numbersCountInTicket: observable,
      liveBetActiveTabIndex: observable,
      maxNumbersCountInTicket: observable,
      selectedTicketsCount: observable,
      tickets: observable.deep,
      kenoOdds: observable.deep,
      lastTickets: observable.deep,
      kenoOddsBySelectedNumbersCount: observable.deep,
      setWinOdd: action.bound,
      setRoundId: action.bound,
      autoSelect: action.bound,
      setTickets: action.bound,
      setKenoOdds: action.bound,
      setWinAmount: action.bound,
      deleteTicket: action.bound,
      setReBetModel: action.bound,
      updateTickets: action.bound,
      setWinNumbers: action.bound,
      setHotNumbers: action.bound,
      setColdNumbers: action.bound,
      setMaxWinAmount: action.bound,
      setNotification: action.bound,
      setLastTickets: action.bound,
      autoFillNumbers: action.bound,
      setIsOpenNumpad: action.bound,
      setIsBetsClosed: action.bound,
      updateWinNumbers: action.bound,
      setPendingTickets: action.bound,
      setTotalWinAmount: action.bound,
      setIsStopAnimation: action.bound,
      setSelectedNumbers: action.bound,
      updateLastResultOdd: action.bound,
      setIsBetButtonActive: action.bound,
      setIsShowMobileResult: action.bound,
      updateSelectedNumbers: action.bound,
      resetNonSelectedNumber: action.bound,
      setWinNumbersForTickets: action.bound,
      addTicketsByTicketCount: action.bound,
      setSelectedTicketsCount: action.bound,
      setNumbersCountInTicket: action.bound,
      setLiveBetActiveTabIndex: action.bound,
      updateTicketsFromLastTicket: action.bound,
      randomGenerateTicketNumbers: action.bound,
    });
    reaction(
      () => this.selectedNumbers,
      (value, previousValue) => {
        if (previousValue.length !== value.length) {
          this.winNumbers = [];
          this.setReBetModel(null);
          this.nonSelectedNumbers = kenoNumbers.filter((el) => !value.includes(el));
        }
      }
    );

    reaction(
      () => userInfoStore.betAmount,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.winNumbers = [];
          this.setPendingTickets();
          this.setReBetModel(null);
        }
      }
    );
    reaction(
      () => this.tickets,
      (value, previousValue) => {
        if (previousValue.length !== value.length) {
          this.setTotalWinAmount(null);
          this.setWinNumbersForTickets([]);
        }
        if (value.length) {
          this.setLiveBetActiveTabIndex(TAB_INDEX.BetSlip);
        }
      }
    );
    reaction(
      () => this.numbersCountInTicket,
      (value, previousValue) => {
        if (previousValue !== value) {
          this.winNumbers = [];
          this.setPendingTickets();
          this.setReBetModel(null);
        }
      }
    );

    reaction(
      () => applicationStore.isKeno8,
      (value) => {
        if (value) {
          this.numbersCountInTicket = applicationStore.isKeno8
            ? SELECTED_NUMBERS_MAX_COUNT_KENO8
            : SELECTED_NUMBERS_MAX_COUNT_KENO10;

          this.maxNumbersCountInTicket = applicationStore.isKeno8
            ? SELECTED_NUMBERS_MAX_COUNT_KENO8
            : SELECTED_NUMBERS_MAX_COUNT_KENO10;
        }
      }
    );
  }

  setIsStopAnimation(bool) {
    this.isStopAnimation = bool;
  }

  setMaxWinAmount(maxWinAmount) {
    this.maxWinAmount = maxWinAmount;
  }

  setIsBetButtonActive(bool) {
    this.isBetButtonActive = bool;
  }

  setLiveBetActiveTabIndex(tabIndex) {
    this.liveBetActiveTabIndex = tabIndex;
  }

  setTotalWinAmount(amount) {
    this.totalWinAmount = amount;
  }

  setIsShowMobileResult(bool) {
    this.isShowMobileResult = bool;
  }

  setRoundId(id) {
    this.roundId = id;
  }

  setIsOpenNumpad(bool) {
    this.isOpenNumpad = bool;
  }

  setColdNumbers(numbers) {
    this.coldNumbers = numbers;
  }

  setHotNumbers(numbers) {
    this.hotNumbers = numbers;
  }

  setReBetModel(reBetModel) {
    this.reBetModel = reBetModel;
  }

  setLastTickets(lastTickets) {
    this.lastTickets = lastTickets;
  }

  setSelectedTicketsCount(value) {
    this.selectedTicketsCount = value;
    this.winNumbers = [];
    this.setReBetModel(null);
    this.addTicketsByTicketCount(value);
  }

  setTickets(tickets) {
    this.tickets = tickets;
  }

  setNumbersCountInTicket(number) {
    this.setPendingTickets();
    this.numbersCountInTicket = number;
  }

  updateLastResultOdd(odd) {
    const updatedLastResult = [...this.lastResultsOdd, odd];
    if (updatedLastResult.length > (isMobile() ? 3 : 5)) {
      updatedLastResult.splice(0, 1);
    }
    this.lastResultsOdd = updatedLastResult;
  }

  setWinOdd(odd) {
    this.winOdd = odd;
  }

  setWinAmount(winAmount) {
    this.winAmount = winAmount;
  }

  updateWinNumbers(number) {
    if (this.winNumbers.includes(number)) {
      return;
    }
    if (this.tickets.some((el) => el.numbers.includes(number))) {
      if (userSettingsStore.isSoundOn) {
        soundService.playMatch();
      }
    }
    this.winNumbersForTickets = [...this.winNumbersForTickets, number];
    this.winNumbers = [...this.winNumbers, number];
  }

  setWinNumbers(winNumbers) {
    this.winNumbers = winNumbers;
  }

  setWinNumbersForTickets(winNumbers) {
    this.winNumbersForTickets = winNumbers;
  }

  setKenoOddsBySelectedNumbersCount(odds) {
    this.kenoOddsBySelectedNumbersCount = odds;
  }

  setKenoOdds(odds) {
    this.kenoOdds = odds;
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  updateSelectedNumbers(numbers) {
    numbers.forEach((number) => {
      const includedNumber = this.selectedNumbers.includes(number);
      this.setPendingTickets();
      if (includedNumber) {
        if (userSettingsStore.isSoundOn) {
          soundService.playClearDelete();
        }
        this.selectedNumbers = this.selectedNumbers.filter((el) => el !== number);
      } else if (this.selectedNumbers.length < this.maxNumbersCountInTicket) {
        if (userSettingsStore.isSoundOn) {
          soundService.playNumberSelection();
        }
        this.selectedNumbers = [...this.selectedNumbers, number];
      } else if (userSettingsStore.isSoundOn) {
        notificationStore.setNotificationType(
          applicationStore.isKeno8 ? MaxNumberCount8 : MaxNumberCount10
        );
        setTimeout(() => {
          notificationStore.setNotificationType(null);
        }, 1000);
        soundService.playNumberCantSelection();
      }
      this.setKenoOddsBySelectedNumbersCount(this.kenoOdds[this.selectedNumbers.length] || {});
    });
  }

  setSelectedNumbers(selectedNumbers) {
    this.selectedNumbers = selectedNumbers;
    this.setKenoOddsBySelectedNumbersCount({});
  }

  resetNonSelectedNumber() {
    this.nonSelectedNumbers = kenoNumbers;
  }

  randomGenerateTicketNumbers() {
    const randomIndex = Math.floor(Math.random() * this.nonSelectedNumbers.length);
    this.updateSelectedNumbers([this.nonSelectedNumbers[randomIndex]]);
    this.nonSelectedNumbers.splice(randomIndex, 1);
  }

  addTicketsByTicketCount(value) {
    this.selectedNumbers = [];
    this.setPendingTickets();
    new Array(value).fill(null).forEach(() => {
      new Array(this.numbersCountInTicket).fill(null).forEach(() => {
        this.randomGenerateTicketNumbers();
      });
      this.updateTickets();
      this.resetNonSelectedNumber();
    });
  }

  updateTickets(isBetAction = false) {
    if (isBetAction && this.tickets.length) {
      this.setSelectedNumbers([]);
      return;
    }
    if (this.selectedNumbers.length) {
      const model = {
        ticketNumber: (this.tickets[0]?.ticketNumber || 0) + 1,
        isWin: false,
        amount: userInfoStore.betAmount,
        numbers: this.selectedNumbers,
        status: BetStatus.Pending,
        possibleWin: parseNumberPrecisionByDoteCount(
          this.kenoOdds[this.selectedNumbers.length][this.selectedNumbers.length],
          userInfoStore.betAmount,
          userInfoStore.precision
        ),
      };
      const tickets = [model, ...this.tickets];
      if (!isBetAction && userSettingsStore.isSoundOn) {
        soundService.playAdd();
        this.setNotification(AddTicketsCode);
      }
      this.setTickets(tickets);
      this.setSelectedNumbers([]);
    }
  }

  setNotification(notificationCode) {
    notificationStore.setNotificationType(notificationCode);
    setTimeout(() => {
      notificationStore.setNotificationType(null);
    }, 1000);
  }

  updateTicketsFromLastTicket() {
    let ticketsData = [...this.lastTickets, ...this.tickets].filter(
      (el) => el.status === BetStatus.Pending
    );
    ticketsData = ticketsData.map((el, index) => ({
      ...el,
      ticketNumber: ticketsData.length - index,
      status: BetStatus.Pending,
    }));
    this.setReBetModel(null);
    this.winNumbers = [];
    this.tickets = ticketsData;
    soundService.playAdd();
    notificationStore.setNotificationType(AddTicketsCode);
    setTimeout(() => {
      notificationStore.setNotificationType(null);
    }, 1000);
  }

  setPendingTickets() {
    this.tickets = this.tickets.filter((el) => el.status === BetStatus.Pending);
  }

  autoFillNumbers() {
    this.setPendingTickets();
    new Array(this.numbersCountInTicket - this.selectedNumbers.length).fill(null).forEach(() => {
      this.randomGenerateTicketNumbers();
    });
  }

  autoSelect() {
    if (this.selectedNumbers.length < this.numbersCountInTicket) {
      this.autoFillNumbers();
    } else {
      this.resetNonSelectedNumber();
      this.selectedNumbers = [];
      this.autoFillNumbers();
    }
  }

  deleteTicket(ticketNumber) {
    this.tickets = this.tickets.filter((el) => el.ticketNumber !== ticketNumber);
  }
}

export const kenoBetStore = new KenoBetStore();
